import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react';
import { NextParsedUrlQuery } from 'next/dist/server/request-meta';

import {
  IBlogPost,
  IGlobalConfiguration,
  IGlobalConfigurationFields,
  ILearningCenterConfiguration,
  ILearningCenterConfigurationFields,
  ILearningCenterArticle,
  IPage,
  IPageFields,
  IPartnerPageConfiguration,
  IPartnerPageConfigurationFields
} from '@/types/contentful';

import { Cookies } from '../../modules/cookies/getServerSideCookies';
import { UserData } from '../../lib/referrals/getUserDataById';
import { usePublicRuntimeConfig } from 'src/hooks/usePublicRuntimeConfig';

export enum envEnum {
  local = 'development',
  development = 'development',
  preview = 'preview',
  staging = 'staging',
  production = 'production'
}

export enum returningUserEnvEnum {
  local = 'local',
  development = 'dev',
  staging = 'staging',
  production = 'production'
}

export type UrlByEnv = {
  [key in envEnum]?: string;
};

export type TeenReferralPlan = {
  name: string;
  price: string;
  show_badge: boolean;
};

export type TeenReferralFeature = {
  badge_image: string;
  badge_copy: string;
};

export type TeenReferralData = {
  name: string;
  features: TeenReferralFeature[];
  message: string;
  plans: TeenReferralPlan[];
};

export interface PageFieldProps {
  page?: IPageFields;
  config?: IGlobalConfigurationFields;
  articleConfig?: ILearningCenterConfigurationFields;
  partnerPageConfig?: IPartnerPageConfigurationFields;
  webregIncident?: boolean;
  cookies?: Cookies;
  query?: any;
  slug?: string | null;
  advocateUserData?: UserData | null;
  teenReferralData?: TeenReferralData;
  minAprAmount?: number;
  maxAprAmount?: number;
  effectiveDateConversion?: any;
  isReturning?: boolean;
  embeddedPosts?: any[];
  appIncident?: boolean;
}

export interface PageProps {
  query?: NextParsedUrlQuery;
  slug?: string | null;
  pageData?: IPage;
  blogPostData?: IBlogPost;
  articleData?: ILearningCenterArticle;
  globalConfig?: IGlobalConfiguration;
  learningCenterConfig?: ILearningCenterConfiguration;
  partnerPageConfig?: IPartnerPageConfiguration;
  cookies?: Cookies;
  advocateUserData?: UserData;
  teenReferralData?: TeenReferralData;
  minAprAmount?: number;
  maxAprAmount?: number;
  effectiveDateConversion?: any;
  embeddedPosts?: any[];
  appIncident?: boolean;
}

export interface PageProvider extends PageProps {
  children?: ReactNode | ReactNode[];
}

export const PageContext = createContext<PageFieldProps>({} as PageFieldProps);

export const usePageDataContext = () => useContext(PageContext);

export function PageDataProvider({
  globalConfig,
  learningCenterConfig,
  partnerPageConfig,
  pageData,
  query,
  slug,
  cookies,
  children,
  advocateUserData,
  teenReferralData,
  minAprAmount,
  maxAprAmount,
  effectiveDateConversion,
  embeddedPosts
}: PageProvider) {
  const [webregIncident, setIncidentResponse] = useState(
    globalConfig?.fields?.enableIncidentResponse ?? false
  );
  const [appIncident, setAppIncident] = useState(
    globalConfig?.fields?.enableAppIncident ?? false
  );

  useEffect(() => {
    window.setIncident = globalConfig?.fields?.enableIncidentResponse
      ? setIncidentResponse
      : globalConfig?.fields?.enableAppIncident
      ? setAppIncident
      : undefined;
  }, []);

  const { nodeEnv } = usePublicRuntimeConfig();
  const cookieName = `__onboarding-root-session-${
    returningUserEnvEnum[nodeEnv as keyof typeof returningUserEnvEnum]
  }`;
  const isReturning =
    Boolean(
      cookies &&
        cookies[cookieName as keyof typeof cookies] !== undefined &&
        cookies[cookieName as keyof typeof cookies] !== null
    ) || cookies?.gl_is_returning_user === 'returning';

  return (
    <PageContext.Provider
      value={{
        config: globalConfig?.fields,
        articleConfig: learningCenterConfig?.fields,
        partnerPageConfig: partnerPageConfig?.fields,
        page: pageData?.fields,
        query,
        slug,
        webregIncident,
        cookies: cookies ?? ({} as Cookies),
        advocateUserData,
        teenReferralData,
        minAprAmount,
        maxAprAmount,
        effectiveDateConversion,
        isReturning,
        embeddedPosts,
        appIncident
      }}
    >
      {children}
    </PageContext.Provider>
  );
}
